/**
 * Onde os avisos passageiros aparecem — canto inferior direito, empilhados.
 *
 * Fica no rodapé e não no topo de propósito: o topo do sistema já tem a
 * saudação, o sino de pendências e o bloco da loja, e um aviso caindo ali
 * cobriria justamente o que a pessoa acabou de clicar. O canto de baixo está
 * sempre livre — inclusive no PDV, que não tem barra lateral.
 *
 * ## O que faz dele acessível
 *
 * `role="status"` com `aria-live="polite"`: o leitor de tela anuncia quando
 * terminar o que está lendo, em vez de interromper. Erro usa `assertive`,
 * porque aí interromper é o certo.
 *
 * O `<ol>` não é preciosismo: quando dois avisos chegam juntos, a ordem
 * importa, e uma lista é o que comunica "são dois, este é o primeiro".
 *
 * Cada aviso tem botão de dispensar. Um aviso que só some por tempo é um aviso
 * que quem lê devagar não consegue tirar da frente.
 */
import { Icone, type NomeIcone } from './Icone'
import { dispensarAviso, useAvisos, type TomAviso } from './avisos'

const ESTILO: Record<TomAviso, { classe: string; icone: NomeIcone }> = {
  // Tint como fundo e o tom ESCURO no texto — a mesma regra do `Selo`, pelo
  // mesmo motivo: o sólido sobre o tint não alcança 4,5:1.
  sucesso: { classe: 'border-ok-suave bg-ok-fundo text-ok-texto', icone: 'check_circle' },
  erro: { classe: 'border-erro-suave bg-erro-fundo text-erro-texto', icone: 'error' },
  info: { classe: 'border-marca-borda bg-marca-fundo-2 text-marca-forte', icone: 'info' },
}

export function BarraDeAvisos() {
  const avisos = useAvisos()

  if (avisos.length === 0) return null

  return (
    <div
      // `pointer-events-none` no invólucro e `auto` no cartão: a faixa cobre
      // um pedaço da tela, e sem isso ela roubaria o clique de quem tentasse
      // usar o que está atrás dela.
      className="pointer-events-none fixed right-5 bottom-5 z-[60] flex w-[360px] max-w-[calc(100vw-2.5rem)] flex-col gap-2"
    >
      <ol className="flex list-none flex-col gap-2 p-0">
        {avisos.map((aviso) => {
          const estilo = ESTILO[aviso.tom]
          return (
            <li
              key={aviso.id}
              role="status"
              aria-live={aviso.tom === 'erro' ? 'assertive' : 'polite'}
              // `items-center` e não `items-start`: o aviso quase sempre é de
              // uma linha, e alinhado ao topo o ícone e o X ficavam colados na
              // borda de cima, com o texto boiando. Quando o texto passa de
              // uma linha, centrar continua sendo o alinhamento certo para um
              // bloco pequeno e simétrico como este.
              className={`pointer-events-auto flex items-center gap-2.5 rounded-sistema border px-4 py-3 shadow-[0_12px_32px_rgba(15,23,42,0.12)] ${estilo.classe}`}
            >
              <Icone nome={estilo.icone} tamanho={18} className="shrink-0" />
              <span className="min-w-0 flex-1 text-[13px] leading-[1.5] font-medium">
                {aviso.texto}
              </span>
              <button
                type="button"
                onClick={() => dispensarAviso(aviso.id)}
                className="-mr-1 shrink-0 self-center rounded-sistema p-1 opacity-60 hover:opacity-100"
              >
                <Icone nome="close" tamanho={15} />
                <span className="sr-only">Dispensar aviso</span>
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
